'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { asBaseWidget, PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  getRecommendationDedupeKey,
  usePageWidgetStoreOptional,
  useSeenProductIds,
  useSelectionMap,
  useWidget,
} from '@src/components/pl-base-widget/contexts/page-widget-store'
import { usePdpColor } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { fetchLazyWidgetsBatch } from '@src/components/pl-base-widget/utils/fetch-lazy-widgets'
import {
  buildAnchorAttributes,
  PRODUCT_ID_KEY,
  useLazyRequestAugmentation,
} from '@src/components/pl-base-widget/utils/pdp-widget-request-builders'
import { useLayoutContext, usePLPageMeta } from '@src/components/pl-page-context'
import { WidgetRenderer } from '@src/components/pl-page-renderer/render-widget'
import { SingleProductCardWidget } from '@src/components/widgets/single-product-card'
import {
  CardTrackingProvider,
  useCardImpressionTracking,
} from '@src/components/widgets/single-product-card/card-tracking-context'
import { useBoytTelemetry } from '@src/components/widgets/use-ia-impression'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Loader, LoaderWrapper } from '@src/quince-components/src'
import gridStyles from '@src/quince-components/src/molecules/productListSectionTwo/infiniteScrollProductList.module.scss'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { productContextInitialState } from '@src/quince-site-pdp/src/contexts/ProductPageContext'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import { getTrackingMetaData as getYouMayAlsoLikeTrackingMetaData } from '@src/quince-site-pdp/src/organisms/productPageSections/youMayAlsoLike/youMayAlsoLike.constants'
import youStyles from '@src/quince-site-pdp/src/organisms/productPageSections/youMayAlsoLike/youMayAlsoLike.module.scss'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { dsContentClass, dsGridClass, dsSectionClass, dsTitleClass } from './product-grid-pdp.styles'

import type { Pagination } from '@lastbrand/storefront-proto-registry/api/v1/common'
import type { Widget } from '@lastbrand/storefront-proto-registry/api/v1/layoutfetch'
import type { ProductListWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { ProductListRequestParams } from '@lastbrand/storefront-proto-registry/api/v1/widgetfetch'

// Base-widget view used to render pagination cards through WidgetRenderer, which isolates
// SingleProductCard's hooks (it reads page context) into their own component instance — required
// because these cards are rendered inside a `.map()`, where a direct render() call would run those
// hooks in this widget's render and violate the rules of hooks as the list grows.
const SingleProductCardBaseWidget = asBaseWidget(SingleProductCardWidget)

// BOYT shows at most `displayLimit` products (the BFF sends this on productListData — 48 today).
// The BFF's cursor pagination keeps returning `hasMore: true` past that limit, so the grid would
// paginate forever — firing unnecessary lazy-widget calls and rendering past the intended limit.
// Enforce the cap client-side: stop fetching once the limit is reached, and never render past it.
// Fallback used only when the BFF omits displayLimit. STRFRNT-15960.
const DEFAULT_BOYT_DISPLAY_LIMIT = 48

// Bail out of pagination after this many consecutive pages that add zero net-new cards after dedupe.
// The BFF can keep returning hasMore=true while handing back only products already shown in the STL /
// MTE carousels above; without this bound the sentinel stays in view and re-fetches forever (a
// blinking loader that never completes). A small streak still lets uniques ranked a page or two deeper
// load before we give up. STRFRNT-15960.
const MAX_CONSECUTIVE_EMPTY_PAGES = 3

/**
 * Stable, order-independent serialization of an anchorAttributes map — sorted keys, so two selections
 * with the same values but assembled in a different order compare equal. SVD's generalization of the
 * single `pdpColor` string the coordinated reset below used to compare directly (STRFRNT-15385).
 */
function serializeAnchorAttributes(anchorAttributes: Record<string, string>): string {
  return Object.keys(anchorAttributes)
    .sort()
    .map(key => `${key}=${anchorAttributes[key]}`)
    .join('&')
}

/**
 * PDP PRODUCT_LIST widget (v2) — "Based On Your Taste" infinite-scroll grid.
 *
 * Widget-of-widget architecture:
 *   - Initial products arrive as pre-rendered SINGLE_PRODUCT_CARD nodes in `renderedChildren`.
 *   - On scroll, fetches the next page via fetchLazyWidgetsBatch, maps the nested
 *     SINGLE_PRODUCT_CARD widgets' productItem data to IProductCard[], and renders them
 *     via the shared ProductCard component — consistent with renderedChildren cards.
 *
 * Container uses youMayAlsoLike.module.scss for correct PDP spacing and title typography.
 * Grid uses infiniteScrollProductList.module.scss for the 4-col (desktop) / 2-col (mobile) layout.
 */
function ProductGridPdp(props: PLTypedWidgetProps<ProductListWidgetData>) {
  const data = props.data.productListData
  // "Based On Your Taste" card impressions must carry the section's own `trackingId`
  // (`based-on-your-taste`) — matching non-PL `you-may-also-like`'s BULK_PRODUCT_CARD_VIEW — instead of
  // the flattened page-level `pdp-product-display`. Register the band's batched stream and publish its
  // tracker to the child leaves via `CardTrackingProvider`.
  // Track the STATIC canonical `module_title` ("Based on your taste"), mirroring non-PL you-may-also-like-ds
  // (`displayTitle = filteredYouMayAlsoLikeProducts.module_title`) — NOT the localized/CMS-driven widget
  // `data.title`, which would diverge from non-PL in non-en-us locales.
  const cardTrackingMetaData = useMemo(
    () =>
      getYouMayAlsoLikeTrackingMetaData(
        productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT4].module_title,
      ),
    [],
  )
  const cardProductViewTracker = useCardImpressionTracking(cardTrackingMetaData)
  const pageMeta = usePLPageMeta()
  const baseLayoutContext = useLayoutContext()
  // Same reco-product-id resolution the INITIAL lazy fetch uses (buy-box-published wins, context is
  // the pre-publish fallback). Page 1 carried `productId`; without this every scroll page dropped it
  // and the BE fell back to its own default — on a bundle that's the Contentful id, not the first
  // child's, so later pages could rank against a different product than page 1.
  const { selectionExtras, effectiveContext: layoutContext } = useLazyRequestAugmentation(baseLayoutContext)
  const recoProductId = selectionExtras[PRODUCT_ID_KEY]
  const store = usePageWidgetStoreOptional()
  const pdpColor = usePdpColor()
  // SVD: anchorAttributes built from the LIVE published selection (all VDA entries), merged with the
  // reserved extras (e.g. __productId) the widget already resolves above — buildAnchorAttributes drops
  // those (`__`-prefixed), same as the initial-fetch builders. Legacy: undefined; the `color` field below
  // still reads `pdpColor` directly, unchanged. Never both on the same request (PL: replaced, not augmented).
  const selectionMap = useSelectionMap()
  const anchorAttributes = useMemo(
    () => (pageMeta.svdEnrichEnabled ? buildAnchorAttributes({ ...selectionMap, ...selectionExtras }) : undefined),
    [pageMeta.svdEnrichEnabled, selectionMap, selectionExtras],
  )
  // Selection signature the coordinated reset below keys on — legacy: `pdpColor` (today's exact
  // semantics, unchanged); SVD: a stable serialization of the anchor map (dynamic VDA keys, so a single
  // static field can't stand in for it). An anchor change now clears paginated seen-ids and freezes
  // pagination exactly like a color change does on V1.
  const selectionSignature = pageMeta.svdEnrichEnabled ? serializeAnchorAttributes(anchorAttributes ?? {}) : pdpColor
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  // Swatch/color-change refetch in flight → overlay a spinner on the grid (SWR keeps the widget
  // mounted with `isRefreshing`), matching the non-PL loader. Distinct from `isFetching`, which is
  // the infinite-scroll pagination loader below. Key by `widget.id` (the store's UUID key), not
  // `widgetId` (API-request key) — see lazy-widget-fetcher.ts:141.
  const storeEntry = useWidget(props.id)
  const isRefreshing = storeEntry?.isRefreshing ?? false
  const entryStatus = storeEntry?.status
  const loaderMinHeight = isDesktop ? '40rem' : '20rem'

  // Reactive subscription — re-renders the component when carousels add to the seen set.
  // A ref shadows it so fetchMore reads the LATEST value without being in its dep array
  // (which would cause runaway fetches: registerProductIds → seenProductIds changes →
  //  fetchMore recreated → useEffect re-fires → another fetch while sentinel is visible).
  const seenProductIds = useSeenProductIds()
  const seenProductIdsRef = useRef(seenProductIds)
  seenProductIdsRef.current = seenProductIds

  const [paginationInfo, setPaginationInfo] = useState<Pagination | undefined>(props.pagination)
  // Store raw SINGLE_PRODUCT_CARD widget objects from pagination so we render
  // through SingleProductCardWidget.render() — same widget-of-widget path as initial cards.
  const [additionalWidgets, setAdditionalWidgets] = useState<Widget[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const isFetchingRef = useRef(false)
  // Consecutive pages that added nothing new after dedupe — used to bail out of a recycling BFF.
  const emptyPageStreakRef = useRef(0)
  // Selection the currently-held paginated state belongs to; a change triggers the coordinated reset
  // below. Legacy: `pdpColor`. SVD: the anchor-map signature (STRFRNT-15385 — generalized from the
  // color-only ref this used to be).
  const paginatedSelectionRef = useRef(selectionSignature)
  // Armed by a selection change, disarmed when the refetch settles (fresh widget OR error). Only the
  // error-settle path needs it — the success path resyncs unconditionally below.
  const awaitingSelectionRefreshRef = useRef(false)
  // Bumped by the selection reset. A fetch captures the epoch when it STARTS and discards its response
  // if the epoch moved while it was in flight — otherwise a slow old-selection page landing after the
  // reset would re-append stale cards, re-register the just-cleared ids, and un-freeze pagination with
  // the dead old-selection cursor, silently undoing the whole coordinated reset.
  const fetchEpochRef = useRef(0)

  // Coordinated selection-change reset. The ids we registered into the store describe the paginated
  // cards we DISPLAY; on a swatch/anchor change those cards are stale (the whole band refetches for the
  // new selection), so drop them and unregister their ids in the same commit — otherwise ids registered
  // under the old selection keep suppressing the same products legitimately re-returned for the new one,
  // and the ghost set compounds with every switch. Pagination is frozen (undefined → no sentinel, no
  // eager fetch against the DEAD old-selection cursor) until the refreshed widget re-seeds it below.
  useEffect(() => {
    if (paginatedSelectionRef.current === selectionSignature) return
    paginatedSelectionRef.current = selectionSignature
    awaitingSelectionRefreshRef.current = true
    fetchEpochRef.current += 1
    emptyPageStreakRef.current = 0
    setAdditionalWidgets([])
    setPaginationInfo(undefined)
    store?.getState().clearPaginatedSeenIds()
  }, [selectionSignature, store])

  // Resync the cursor whenever the store widget is actually replaced. `props.pagination` is
  // identity-stable across re-renders and dedupe rewrites (spreads copy the reference) — it only
  // changes when upsertReady lands a refetched widget or markResolvedEmpty/markPaginationExhausted
  // flip it. Resyncing UNCONDITIONALLY (not via a one-shot flag) keeps the cursor consistent with
  // the DISPLAYED widget even when overlapping color refetches resolve out of order: a stale
  // first response can't "consume" the reseed — the last landed widget wins for cards AND cursor,
  // matching the store's last-writer-wins entry semantics.
  useEffect(() => {
    awaitingSelectionRefreshRef.current = false
    setPaginationInfo(props.pagination)
  }, [props.pagination])

  // A FAILED selection refetch never replaces the widget (markError leaves `entry.widget` untouched),
  // so the resync above can't fire and pagination would stay frozen forever while the band shows its
  // stale (SWR) cards. Settle explicitly: seed from the surviving pagination with hasMore:false —
  // the band stops waiting instead of dangling. Scoped to the selection-change window: an ordinary
  // scroll-fetch error outside it must not clobber a still-valid cursor.
  useEffect(() => {
    if (!awaitingSelectionRefreshRef.current || entryStatus !== 'error') return
    awaitingSelectionRefreshRef.current = false
    setPaginationInfo(props.pagination ? { ...props.pagination, hasMore: false } : undefined)
  }, [entryStatus, props.pagination])

  // On unmount, free our registered ids. Covers the path the selection effect can't: a swatch refetch
  // that resolves EMPTY collapses the band (markResolvedEmpty → isEmpty), destroying this component
  // state — without this cleanup the dead cards' ids would suppress refreshed bands forever.
  // Latest-ref + empty deps: the cleanup must run at UNMOUNT only, never on a re-render.
  const storeRef = useRef(store)
  storeRef.current = store
  useEffect(() => {
    return () => {
      storeRef.current?.getState().clearPaginatedSeenIds()
    }
  }, [])

  // Total cards already loaded = server-rendered initial page + everything pulled by infinite scroll.
  const renderedCount = props.renderedChildren?.length ?? 0
  const totalLoaded = renderedCount + additionalWidgets.length

  // "Based on your taste" IA telemetry — section scroll-into-view impression (you_may_also_like IS_VISIBLE
  // + recommendationView) and the per-card PRODUCT_CLICK config, both mirroring non-PL you-may-also-like-ds.
  // Lives in the shared PDP impression hook alongside the other reco/impression telemetry.
  const { sectionImpressionRef, cardClickTrackConfig } = useBoytTelemetry({
    cardTrackingMetaData,
    bandTitle: data?.title,
    widgetId: props.widgetId,
    productCount: totalLoaded,
  })

  // Server-driven display cap (falls back to 48 if the BFF omits it).
  const displayLimit = data?.displayLimit && data.displayLimit > 0 ? data.displayLimit : DEFAULT_BOYT_DISPLAY_LIMIT

  // Only paginate while the BFF has more AND we're under the display cap. `canFetchMore` gates the
  // fetch guard, the scroll effect, and the sentinel, so at the limit the sentinel unmounts and no
  // further lazy-widget calls fire. STRFRNT-15960.
  const hasMore = paginationInfo?.hasMore ?? false
  const canFetchMore = hasMore && totalLoaded < displayLimit

  const fetchMore = useCallback(async () => {
    if (isFetchingRef.current || !canFetchMore) return
    isFetchingRef.current = true
    setIsFetching(true)
    const epoch = fetchEpochRef.current

    try {
      // SVD: anchorAttributes (built above from the live selection map) instead of color (never both —
      // PL: "in svd color will be replaced by anchorAttributes"). Legacy: color, unchanged. Mirrors
      // PDP_LAZY_WIDGET_REQUEST_BUILDERS' branch.
      const resolved = await fetchLazyWidgetsBatch(
        [
          {
            id: props.widgetId,
            type: WidgetType.PRODUCT_LIST,
            request: {
              // PDP: only color/anchorAttributes + cursor needed. No sorting/filters (BE uses its own
              // ranking).
              productList: {
                // `filters` is required by proto even though PDP has no filters — send empty array.
                filters: [],
                // Required map since registry 3.15.0. When empty it is present in the JSON body as `{}`,
                // which proto3 JSON parsing reads as unset — the BE sees the same request as the old
                // only-when-non-empty spread.
                anchorAttributes: anchorAttributes ?? {},
                ...(!pageMeta.svdEnrichEnabled && pdpColor ? { color: pdpColor } : {}),
                // Mirrors the initial fetch's builder (`PDP_LAZY_WIDGET_REQUEST_BUILDERS`): omit rather
                // than send '' so the BE keeps its own default when no id is known.
                ...(recoProductId ? { productId: recoProductId } : {}),
                pagination: paginationInfo,
              } satisfies ProductListRequestParams,
            },
          },
        ],
        pageMeta,
        layoutContext,
      )

      // Color changed while this page was in flight → it belongs to the OLD color; drop it whole
      // (no cards, no id registration, no cursor advance — the reset already froze pagination).
      if (fetchEpochRef.current !== epoch) return

      const nextPagination = resolved[0]?.pagination
      // Keep as raw Widget objects — render through SingleProductCardWidget.render()
      // so pagination cards go through the same widget-of-widget path as initial cards.
      const nestedWidgets: Widget[] = resolved[0]?.widgets ?? []

      // Filter against everything already on screen AND within this page itself (a server page that
      // repeats a product would otherwise render both copies — neither is in the seen set yet).
      const pageSeenIds = new Set<string>()
      const filteredWidgets = nestedWidgets.filter(widget => {
        const key = getRecommendationDedupeKey(widget)
        if (key === undefined) return true
        if (seenProductIdsRef.current.has(key) || pageSeenIds.has(key)) return false
        pageSeenIds.add(key)
        return true
      })

      const newIds = filteredWidgets.map(w => getRecommendationDedupeKey(w)).filter((id): id is string => Boolean(id))

      // Register the new page's product IDs into the store so HPL reserve backfill
      // never re-surfaces a product already loaded in the grid.
      if (newIds.length > 0) {
        store?.getState().registerProductIds(newIds)
      }

      const addedNew = filteredWidgets.length > 0
      if (addedNew) {
        emptyPageStreakRef.current = 0
        setAdditionalWidgets(prev => [...prev, ...filteredWidgets])
      } else {
        emptyPageStreakRef.current += 1
      }

      // Keep advancing the cursor while the server has more — including through pages the dedupe
      // empties, so uniques ranked deeper still load — UNLESS the server returns an empty page or has
      // handed back only already-seen products for too many pages in a row. Without this bound the
      // BFF's persistent hasMore=true + fully-overlapping recos re-fetch forever (a blinking loader
      // that never completes). The deduped display cap in `canFetchMore` stops the happy path.
      // STRFRNT-15960.
      const exhausted = nestedWidgets.length === 0 || emptyPageStreakRef.current >= MAX_CONSECUTIVE_EMPTY_PAGES
      if (nextPagination && !exhausted) {
        setPaginationInfo(nextPagination)
      } else {
        setPaginationInfo(prev => (prev ? { ...prev, hasMore: false } : prev))
      }
    } catch (error) {
      // Stale-epoch failure: the request was for a color we already abandoned — don't let it flip
      // hasMore on the NEW color's (re-seeded) pagination, and skip the Sentry noise.
      if (fetchEpochRef.current !== epoch) return
      // Halt pagination on failure, but surface it — without this the network/500/malformed
      // failure is indistinguishable from "reached end of list" in Sentry/Datadog.
      console.error('Failed to load more PDP grid products:', error)
      captureException(error instanceof Error ? error : new Error(String(error)), {
        module: MODULES.PL,
        feature: FEATURES.WIDGET_PAGINATION,
        additionalTags: {
          trigger: 'pdp_grid_infinite_scroll',
          pageType: pageMeta.pageType ?? 'unknown',
          widgetId: props.widgetId,
          pathname: pageMeta.pathname,
        },
        extraInfo: {
          source: 'ProductGridPdp',
          slug: pageMeta.seoData?.slug ?? pageMeta.pathname,
          paginationCursor: paginationInfo?.cursor ?? '',
        },
      })
      setPaginationInfo(prev => (prev ? { ...prev, hasMore: false } : prev))
    } finally {
      isFetchingRef.current = false
      setIsFetching(false)
    }
    // seenProductIds intentionally excluded — read via ref to avoid recreating fetchMore
    // on every registerProductIds call which would cause runaway scroll fetches.
  }, [
    canFetchMore,
    paginationInfo,
    pageMeta,
    layoutContext,
    props.widgetId,
    store,
    pdpColor,
    anchorAttributes,
    recoProductId,
  ])

  // eslint-disable-next-line i18next/no-literal-string -- IntersectionObserver rootMargin string, not user-facing copy
  const [intersectRef, isIntersecting] = useIntersect(null, '0px 0px 800px 0px')

  useEffect(() => {
    if (isIntersecting && canFetchMore && !isFetchingRef.current) {
      void fetchMore()
    }
  }, [isIntersecting, canFetchMore, fetchMore])

  // Eager recovery when the initial page fully deduped to zero cards. On a heavily-overlapping PDP
  // (e.g. a bags product where every reco band is bags), the store's cross-widget dedupe can strip
  // ALL of this grid's server cards — the higher-priority carousels above already claimed them. The
  // band would then mount empty and, because the scroll sentinel only fires on intersection, never
  // backfill — so BOYT silently disappears even though the server has more pages (total >> 48).
  // Eagerly pull the next page(s) until at least one unique card lands or pagination exhausts (the
  // MAX_CONSECUTIVE_EMPTY_PAGES guard in fetchMore flips hasMore off, so this is bounded). The
  // renderer keeps the band mounted while hasMore is true (see isEmpty). STRFRNT-16003.
  useEffect(() => {
    if (totalLoaded === 0 && canFetchMore && !isFetchingRef.current) {
      void fetchMore()
    }
  }, [totalLoaded, canFetchMore, fetchMore])

  // If that recovery exhausts futilely (every page a dupe / fetch error) with ZERO cards displayed,
  // sync the exhaustion back to the store: `isEmpty` reads the STORE widget's `pagination.hasMore`
  // (still true — only local state flipped), so without this the renderer keeps an empty
  // `<div data-widget-type>` shell mounted forever, breaking the sibling section-divider CSS the
  // isEmpty skip exists to protect. Guarded on totalLoaded === 0: flipping the store's hasMore while
  // backfilled cards exist would make isEmpty true and unmount the band, losing those cards (they
  // live in component state, not renderedChildren). STRFRNT-16003.
  useEffect(() => {
    if (totalLoaded === 0 && !hasMore && paginationInfo !== undefined) {
      store?.getState().markPaginationExhausted(props.id)
    }
  }, [totalLoaded, hasMore, paginationInfo, store, props.id])

  // Initial products are deduped at the store level (dedupeEntries runs on every setWidgets call).
  // PLWidgetNodeV2 re-renders with fresh props.renderedChildren when the store updates, so the
  // renderer handles initial dedup reactively without needing a filter here.
  const renderedCards = props.renderedChildren ?? []

  if (renderedCards.length === 0 && additionalWidgets.length === 0) return null

  // Enforce the 48 cap on what actually renders: show at most 48 initial cards, then fill the
  // remaining slots from the paginated widgets. A page batch that lands just over 48 (page size is
  // server-controlled, so the last fetch can overshoot) is trimmed here rather than displayed.
  const displayedCards = renderedCards.slice(0, displayLimit)
  const displayedAdditional = additionalWidgets.slice(0, Math.max(0, displayLimit - displayedCards.length))

  const isDS = shouldUseDesignSystem()
  const gridColCount = isDesktop ? 4 : 2

  // ── DS path ──────────────────────────────────────────────────────────────
  if (isDS) {
    return (
      <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
        {/* Same section locator as the non-PL YMAL section (PL and non-PL never co-mount); cards are
            numbered by absolute grid order, continuing through the paginated batches. */}
        <section
          ref={sectionImpressionRef}
          className={dsSectionClass}
          data-testid={TEST_LOCATORS.YOU_MAY_ALSO_LIKE_SECTION}
        >
          <div className={dsContentClass}>
            {data?.title ? <div className={dsTitleClass}>{data.title}</div> : null}
            <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
              <div className={dsGridClass}>
                {displayedCards.map((child, index) => (
                  <div
                    key={`${child.id || child.type}-${index}`}
                    data-testid={TEST_LOCATORS.SECTION_PRODUCT(
                      TEST_LOCATORS.PDP_SECTIONS.YOU_MAY_ALSO_LIKE_PRODUCT_PREFIX,
                      index + 1,
                    )}
                  >
                    {child.node}
                  </div>
                ))}
                {displayedAdditional.map((widget, index) => (
                  <div
                    key={`pagination-${index}`}
                    data-widget-type="SINGLE_PRODUCT_CARD"
                    data-testid={TEST_LOCATORS.SECTION_PRODUCT(
                      TEST_LOCATORS.PDP_SECTIONS.YOU_MAY_ALSO_LIKE_PRODUCT_PREFIX,
                      displayedCards.length + index + 1,
                    )}
                  >
                    <WidgetRenderer widgetProps={widget} render={SingleProductCardBaseWidget.render} />
                  </div>
                ))}
              </div>
            </LoaderWrapper>
            <Loader showLoader={isFetching} />
            {canFetchMore && !isFetching && (
              <div ref={intersectRef} style={{ height: '1px', width: '100%' }} aria-hidden="true" />
            )}
          </div>
        </section>
      </CardTrackingProvider>
    )
  }

  // ── Non-DS path ───────────────────────────────────────────────────────────
  return (
    <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
      <div
        ref={sectionImpressionRef}
        className={classNames(youStyles.youMayAlsoLike, youStyles.youMayAlsoLike_newPdpDesignV2)}
      >
        {data?.title ? (
          <h2 className={classNames(youStyles.youMayAlsoLike__title, youStyles.youMayAlsoLike__title_newPdpDesignV2)}>
            {data.title}
          </h2>
        ) : null}
        <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
          <div
            className={gridStyles.infiniteScrollWrapper}
            style={{ gridTemplateColumns: `repeat(${gridColCount}, 1fr)` }}
          >
            {displayedCards.map((child, index) => (
              <div key={`${child.id || child.type}-${index}`}>{child.node}</div>
            ))}

            {displayedAdditional.map((widget, index) => (
              <div key={`pagination-${index}`} data-widget-type="SINGLE_PRODUCT_CARD">
                <WidgetRenderer widgetProps={widget} render={SingleProductCardBaseWidget.render} />
              </div>
            ))}
          </div>
        </LoaderWrapper>

        <Loader showLoader={isFetching} />
        {canFetchMore && !isFetching && (
          <div ref={intersectRef} style={{ height: '1px', width: '100%' }} aria-hidden="true" />
        )}
      </div>
    </CardTrackingProvider>
  )
}

// Reserves layout space while the LAZY fetch is in-flight, preventing a jump when cards land. A grid
// that resolves with no products is reconciled to resolved-empty by the lazy fetcher (markResolvedEmpty),
// so this placeholder never gets stuck on screen for an empty terminal state. STRFRNT-15789.
const gridPlaceholder = <div style={{ height: '80rem', width: '100%' }} />

export const ProductGridPdpWidget: PLTypedWidgetInterface<ProductListWidgetData, WidgetType.PRODUCT_LIST> = {
  id: WidgetType.PRODUCT_LIST,
  errorBoundary: PLBaseErrorBoundary,
  render: ProductGridPdp,
  suspenseFallback: gridPlaceholder,
  // No initial cards → render() returns null (additionalWidgets only ever populate AFTER mount via
  // infinite scroll, so an empty initial list can't grow). Skip the shell to avoid an empty
  // `<div data-widget-type>`.
  // Skip the band only when it has no cards AND the server has no more pages. When the initial page
  // deduped to zero but `hasMore` is true, keep it mounted so the eager-recovery effect can backfill
  // from the next page(s) instead of the band silently vanishing on overlapping PDPs. STRFRNT-16003.
  isEmpty: props => (props.renderedChildren?.length ?? 0) === 0 && !(props.pagination?.hasMore ?? false),
}
